import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import countries from './countries';
import './style.css';

// Flag images (emoji flags don't render on Windows)
const flagUrl = (iso) => `https://flagcdn.com/w40/${iso}.png`;

const MENU_MAX_HEIGHT = 300; // search box + list
const GAP = 6;

// Searchable country code dropdown shown inside the mobile number input.
// `value` / `onChange` work with the country object ({ name, iso, dial }).
//
// The list is rendered in a portal on <body> with position: fixed. Inside the form it was
// clipped by the form's `overflow: hidden` (bottom rows unreachable) and repainted with the
// form's backdrop blur (flicker).
export default function CountryCodeSelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return countries;
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.startsWith(q) || c.iso === q
    );
  }, [search]);

  // Place the menu under the whole input field (same left edge and width).
  // Opens upwards when there isn't enough room below.
  const updatePosition = () => {
    const field = wrapRef.current?.closest('.input-wrap') || wrapRef.current;
    if (!field) return;
    const r = field.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom - GAP;
    const spaceAbove = r.top - GAP;
    const openUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const next = {
      left: Math.round(r.left),
      width: Math.round(r.width),
      maxHeight: Math.round(Math.min(MENU_MAX_HEIGHT, (openUp ? spaceAbove : spaceBelow) - 8)),
      top: openUp ? undefined : Math.round(r.bottom + GAP),
      bottom: openUp ? Math.round(window.innerHeight - r.top + GAP) : undefined,
    };
    // Only re-render when something actually moved
    setPos((prev) =>
      prev && Object.keys(next).every((k) => prev[k] === next[k]) ? prev : next
    );
  };

  // Measure before paint so the menu never flashes at the wrong spot, then keep
  // following the field every frame (covers scroll, resize and the form's own
  // slide-in animation, which moves the field without any scroll/resize event)
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
    let frame;
    const follow = () => {
      updatePosition();
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus the search box once the menu is on screen.
  // preventScroll: focusing must not scroll the page (that caused a jump)
  const menuShown = open && pos !== null;
  useEffect(() => {
    if (menuShown) searchRef.current?.focus({ preventScroll: true });
  }, [menuShown]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = (country) => {
    onChange(country);
    setOpen(false);
    setSearch('');
  };

  // Rows don't depend on the menu position, so moving the menu doesn't re-render 200+ rows
  const rows = useMemo(
    () =>
      filtered.map((c) => (
        <li key={c.iso}>
          <button
            type="button"
            role="option"
            aria-selected={c.iso === value.iso}
            className={`ccs-option${c.iso === value.iso ? ' selected' : ''}`}
            onClick={() => select(c)}
          >
            <img className="ccs-flag" src={flagUrl(c.iso)} alt="" loading="lazy" width="20" height="14" />
            <span className="ccs-name">{c.name}</span>
            <span className="ccs-dial">+{c.dial}</span>
          </button>
        </li>
      )),
    [filtered, value.iso] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="ccs" ref={wrapRef}>
      <button
        type="button"
        className="ccs-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country code: ${value.name} +${value.dial}`}
      >
        <img className="ccs-flag" src={flagUrl(value.iso)} alt="" />
        <span>+{value.dial}</span>
        <svg className={`ccs-chevron${open ? ' open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          className={`ccs-menu${pos.bottom !== undefined ? ' up' : ''}`}
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
        >
          <input
            ref={searchRef}
            type="text"
            className="ccs-search"
            placeholder="Search country or code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              // Enter picks the first match instead of submitting the form
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered[0]) select(filtered[0]);
              }
            }}
          />
          <ul className="ccs-list" role="listbox">
            {rows}
            {filtered.length === 0 && <li className="ccs-empty">No country found</li>}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
